import InsertLinkOutlinedIcon from "@mui/icons-material/InsertLinkOutlined";
import { TableCell, TableRow, Tooltip } from "@mui/material";
import MessageCircle from "assets/img/svg/messageCircle";
import classNames from "classnames";
import type { LastElementTableAPIData } from "components/shared/LastElementTable/type";
import ManagementTableAuthorComponent from "components/shared/ManagementTableAuthorComponent";
import ManagementTableTitleComponent from "components/shared/ManagementTableTitleComponent";
import type { IStatusRule } from "components/shared/StatusChip";
import StatusChip from "components/shared/StatusChip";
import StyledCheckbox from "components/shared/StyledCheckbox";
import type { WhiteDropdownMenuOptions } from "components/shared/WhiteDropdownMenu";
import { WhiteDropdownMenu } from "components/shared/WhiteDropdownMenu";
import { formatLocaleDateString, formatNumber, getPeriodName, isPeriodClosed } from "helpers/utils";
import useWindowSize from "hooks/useWindowSize";
import type { Dispatch, SetStateAction } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { ManagementImpactKind } from "components/AdvanceManagement/ManagementPreviewModal";
import type { IAdvanceSlice } from "store/advancePaymentSlice";
import type { TPeriod } from "store/periodStore";
import { useAppSelector } from "store/store";
import { PaymentStatus } from "store/types";

import variables from "../../../../assets/styles/_variables.module.scss";

interface ITableBodyRow {
    index: number;
    slice: IAdvanceSlice;
    allSlices: IAdvanceSlice[];
    selectedData: {
        allSelected: boolean;
        setAllSelected: Dispatch<SetStateAction<boolean>>;
        handleSetAllSelected: (e: React.ChangeEvent<HTMLInputElement>) => void;
        selectedRows: number[];
        handleSelectRow: (e: React.ChangeEvent<HTMLInputElement>, id: number) => void;
    };
    APIData: LastElementTableAPIData;
    currentSlice: IAdvanceSlice | null;
    setCurrentSlice: Dispatch<SetStateAction<IAdvanceSlice | null>>;
    showCustomStatusColumn: boolean;
    customStatusById: Map<number, string>;
    tableBodyFunctions: {
        handleOpenDeleteModal: () => Promise<void>;
        handleOpenPaymentModal: (slice?: IAdvanceSlice) => void;
        handleOpenUpdateModal: ({ type }: { type: "advance" | "package" }) => Promise<void>;
        setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
        handleOpenManagementPreviewModal: (slice: IAdvanceSlice, impactKind: ManagementImpactKind) => void;
    };
}

const tBodyStyles = {
    padding: "6px 12px",
};

function periodForId(periods: TPeriod[], periodId: number | null | undefined): TPeriod | undefined {
    if (!periodId) return undefined;
    return periods.find((p) => p.id === periodId);
}

function sliceResourcePath(slice: Pick<IAdvanceSlice, "workId" | "titleId">): string | undefined {
    if (slice.workId) return `/works/${slice.workId}`;
    if (slice.titleId) return `/titles/${slice.titleId}`;
    return undefined;
}

/**
 * Body row within AdvancePaymentsTable.
 */
const TableBodyRow = ({
    slice,
    allSlices,
    index,
    selectedData,
    APIData,
    currentSlice,
    setCurrentSlice,
    showCustomStatusColumn,
    customStatusById,
    tableBodyFunctions,
}: ITableBodyRow) => {
    const [t] = useTranslation();
    const windowSize = useWindowSize();
    const nav = useNavigate();
    const { periods, activePeriodData } = useAppSelector((state) => state.period);

    const { selectedRows, handleSelectRow } = selectedData;
    const { handleQuery } = APIData;
    const {
        handleOpenDeleteModal,
        handleOpenPaymentModal,
        handleOpenUpdateModal,
        setIsDrawerOpen,
        handleOpenManagementPreviewModal,
    } = tableBodyFunctions;

    const managementImpactKind: ManagementImpactKind | null = slice.legacyImpactBalanceN1TpdId
        ? "balanceN1"
        : slice.impactGrossDutyTpdId
          ? "grossDuty"
          : null;

    const resourcePath = sliceResourcePath(slice);
    const navigateToSliceResource = resourcePath ? () => nav(resourcePath) : undefined;

    const handleTypeLabelClick = () => {
        if (!managementImpactKind) return;
        handleOpenManagementPreviewModal(slice, managementImpactKind);
    };

    const handleTypeLabelKeyDown = (event: React.KeyboardEvent) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        handleTypeLabelClick();
    };

    // Compute the group for current slice: forfait groups by paymentId; otherwise by connection
    const slicesGroup = useMemo((): IAdvanceSlice[] => {
        if (slice.isForfait) {
            return allSlices.filter(
                (el) => el.termId === slice.termId && el.paymentId === slice.paymentId && !!el.isForfait,
            );
        }
        const shouldBeConnected = Boolean(slice.connectedTPD);
        return allSlices.filter(
            (el) => el.termId === slice.termId && Boolean(el.connectedTPD) === shouldBeConnected && !el.isForfait,
        );
    }, [slice, allSlices]);

    // Depends on `periods` and `t`, not only `slice` (language / period list changes)
    const typeColumnTooltip = useMemo(() => {
        if (slice.legacyImpactBalanceN1TpdId) {
            const period = periodForId(periods, slice.legacyImpactBalanceN1TpdPeriodId);
            const periodLabel = period ? getPeriodName({ period }) : "—";
            return t("AdvancePayment.TypeTooltipImpactedPeriod", {
                impactTarget: t("AdvancePayment.N1Balance"),
                period: periodLabel,
            });
        }
        if (slice.impactGrossDutyTpdId) {
            const period = periodForId(periods, slice.impactGrossDutyPeriodId);
            const periodLabel = period ? getPeriodName({ period }) : "—";
            return t("AdvancePayment.TypeTooltipImpactedPeriod", {
                impactTarget: t("AdvancePayment.ManagementPreviewSliceLinkAdvanceGrossDuty"),
                period: periodLabel,
            });
        }
        return undefined;
    }, [slice, periods, t]);
    const sliceIndex = slicesGroup.findIndex((el) => el.id === slice.id);
    const customStatusName = slice.customStatusId != null ? customStatusById.get(slice.customStatusId) : undefined;

    const groupContinuesBelow = index < allSlices.length - 1 && slicesGroup.includes(allSlices[index + 1]);

    const typeLabel = slice.isForfait ? "F" : "A";
    const typeLabelContent = (
        <span className={classNames("advance-type-label", { "advance-type-label--linked": typeColumnTooltip })}>
            {typeColumnTooltip ? (
                <InsertLinkOutlinedIcon className="advance-type-label__icon" />
            ) : (
                <span className="advance-type-label__icon-slot" aria-hidden />
            )}
            {typeLabel}
        </span>
    );

    /**
     * Rules for payment status column chip.
     */
    const paymentRules: Record<number, IStatusRule> = {
        [PaymentStatus.PAID]: {
            color: "green",
            text: t("AdvancePayment.Paid"),
        },
        [PaymentStatus.TO_PAY]: {
            color: "orange",
            text: t("Filters.ForTransfer"),
        },
        [PaymentStatus.NOT_PAID]: {
            color: "red",
            text: t("AdvancePayment.NotPaid"),
        },
    };

    const sliceMenuOptions = (slice: IAdvanceSlice): WhiteDropdownMenuOptions[] => {
        return [
            {
                id: 1,
                name: t("Management.ShowStatement"),
                hide: currentSlice?.status === PaymentStatus.PAID,
                action: () => {
                    setIsDrawerOpen(true);
                },
            },
            {
                id: 2,
                name: t(`AdvancePayment.Update_${slice.isForfait ? "package" : "advance"}`),
                disabled: disableUpdateOption(currentSlice),
                tooltip: getUpdateDisabledTooltip(currentSlice),
                action: () => handleOpenUpdateModal({ type: currentSlice?.isForfait ? "package" : "advance" }),
            },
            {
                id: 3,
                name: t("Beneficiars.Delete"),
                action: () => handleOpenDeleteModal(),
            },
        ];
    };

    /**
     * Only disable update option if advance linked to a TPD,  period is closed & advance is paid.
     */
    const disableUpdateOption = (slice: IAdvanceSlice | null) => {
        if (slice?.isTermDeleted) return true;
        if (slice && slice.connectedTPD) {
            return isPeriodClosed(slice.periodId, periods);
        }
        return false;
    };

    const getUpdateDisabledTooltip = (slice: IAdvanceSlice | null) => {
        if (!disableUpdateOption(slice)) return "";
        if (slice?.isTermDeleted) return t("BalanceUpdate.AdvancePage.OptionDisabledTermDeleted");
        return t("BalanceUpdate.AdvancePage.OptionDisabled");
    };

    return (
        <TableRow sx={tBodyStyles} key={`advance-payment-${slice.id}`} style={{ position: "relative" }}>
            {
                <>
                    <TableCell
                        sx={{
                            textAlign: "center",
                            padding: "10px",
                            position: "sticky",
                            left: 0,
                            minWidth: "36px",
                        }}
                        className={classNames({
                            noBorder: groupContinuesBelow,
                        })}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: variables["background-one"],
                        }}
                    >
                        <StyledCheckbox
                            onChange={(e) => handleSelectRow(e, slice.id)}
                            checked={selectedRows.includes(slice.id)}
                            size="small"
                        />
                    </TableCell>
                    <TableCell
                        align="left"
                        style={{
                            backgroundColor: variables["background-one"],
                            maxWidth: "200px",
                            position: "sticky",
                            minWidth: "142px",
                            left: "36px",
                            paddingInline: "10px",
                        }}
                        className={classNames({
                            noBorder: groupContinuesBelow,
                        })}
                    >
                        <ManagementTableAuthorComponent
                            author={slice.author}
                            representative={slice.representative}
                            beneficiaryId={slice.beneficiaryId}
                            avatarUrl={slice.beneficiaryAvatarUrl}
                            handleQuery={handleQuery}
                            page="advance"
                            showAvatar={false}
                        />
                    </TableCell>
                    <TableCell
                        align="left"
                        style={{
                            backgroundColor: variables["background-one"],
                            position: "sticky",
                            minWidth: "130px",
                            left: "178px",
                            paddingInline: "10px 5px",
                        }}
                        className={classNames({
                            noBorder: groupContinuesBelow,
                        })}
                    >
                        <ManagementTableTitleComponent
                            nav={navigateToSliceResource}
                            isbn={slice.legacyTermTitleEan13}
                            title={slice.title}
                            titleCoverUrl={slice.titleAvatarUrl}
                            isTermDeleted={slice.isTermDeleted}
                            handleQuery={handleQuery}
                            sx={{ marginLeft: "0px", width: "145px" }}
                            titleStyle={{ width: "25px" }}
                        />
                    </TableCell>
                    <TableCell
                        style={{
                            backgroundColor: variables["background-one"],
                            borderRight: "2px solid var(--border-color)",
                            position: "sticky",
                            left: "338px",
                            right: "495px",
                        }}
                        className={classNames({
                            noBorder: groupContinuesBelow,
                        })}
                        align="right"
                    >
                        <span>{`${sliceIndex + 1}/${slicesGroup.length}`}</span>
                    </TableCell>
                    <TableCell
                        className={classNames({
                            noBorder: groupContinuesBelow,
                        })}
                    >
                        {typeColumnTooltip ? (
                            <Tooltip title={typeColumnTooltip} arrow>
                                <span
                                    className={classNames("advance-type-label", "advance-type-label--linked")}
                                    role="button"
                                    tabIndex={0}
                                    onClick={handleTypeLabelClick}
                                    onKeyDown={handleTypeLabelKeyDown}
                                >
                                    <InsertLinkOutlinedIcon className="advance-type-label__icon" />
                                    {typeLabel}
                                </span>
                            </Tooltip>
                        ) : (
                            typeLabelContent
                        )}
                    </TableCell>
                    <TableCell
                        align="right"
                        className={classNames({
                            noBorder: groupContinuesBelow,
                        })}
                        style={{ paddingLeft: "5px" }}
                    >
                        <div key={`advance-payment-grossAmount-${slice.id}`}>
                            {formatNumber(+slice.amount / 100, {
                                isCurrency: true,
                            })}
                        </div>
                    </TableCell>
                    <TableCell
                        align="right"
                        className={classNames({
                            noBorder: groupContinuesBelow,
                        })}
                        style={{ paddingLeft: "5px" }}
                    >
                        <div key={`advance-payment-netAmount-${slice.id}`}>
                            {formatNumber(+slice.netToPay / 100, {
                                isCurrency: true,
                            })}
                        </div>
                    </TableCell>
                    <TableCell
                        align="right"
                        className={classNames({
                            noBorder: groupContinuesBelow,
                        })}
                    >
                        {formatLocaleDateString({
                            date: slice.paymentDate ?? "",
                            options: { numeric: true, timeZone: "UTC" },
                        })}
                    </TableCell>
                    <TableCell
                        align="left"
                        className={classNames({
                            noBorder: groupContinuesBelow,
                        })}
                    >
                        <StatusChip status={slice.status} statusRules={paymentRules} />
                    </TableCell>
                    {showCustomStatusColumn && (
                        <TableCell
                            align="left"
                            className={classNames({
                                noBorder: groupContinuesBelow,
                            })}
                        >
                            <Tooltip title={customStatusName ?? ""} arrow>
                                <span
                                    style={{
                                        display: "inline-block",
                                        maxWidth: "160px",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {customStatusName ?? "-"}
                                </span>
                            </Tooltip>
                        </TableCell>
                    )}
                    <TableCell
                        align="left"
                        className={classNames({
                            noBorder: groupContinuesBelow,
                        })}
                    >
                        <Tooltip title={slice.comment || ""} arrow>
                            {windowSize.width > 1600 ? (
                                <span
                                    style={{
                                        textOverflow: "ellipsis",
                                        overflow: "hidden",
                                        maxWidth: "90px",
                                        display: "inline-block",
                                        textWrap: "nowrap",
                                        cursor: "pointer",
                                    }}
                                >
                                    {slice.comment || "-"}
                                </span>
                            ) : (
                                <Tooltip title={slice.comment}>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        {slice.comment ? <MessageCircle /> : "-"}
                                    </div>
                                </Tooltip>
                            )}
                        </Tooltip>
                    </TableCell>
                    <TableCell
                        className={classNames({
                            noBorder: groupContinuesBelow,
                        })}
                        sx={{ minWidth: "155px" }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <WhiteDropdownMenu
                                options={sliceMenuOptions(slice)}
                                buttonName={
                                    slice.status !== PaymentStatus.NOT_PAID
                                        ? t("PartialPayment.Actions.SeeStatement_2")
                                        : t("PartialPayment.Actions.Pay")
                                }
                                onMenuClick={() => {
                                    setCurrentSlice(slice);
                                }}
                                mainAction={
                                    slice.status !== PaymentStatus.NOT_PAID
                                        ? () => {
                                              setCurrentSlice(slice);
                                              setIsDrawerOpen(true);
                                          }
                                        : () => handleOpenPaymentModal(slice)
                                }
                                minWidth="120px"
                            />
                        </div>
                    </TableCell>
                </>
            }
        </TableRow>
    );
};

export default TableBodyRow;
